import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { AppLayout } from '@/layout/AppLayout';
import { SettingsLayout } from '@/layout/SettingsLayout';
import {
  RequireAuth,
  RedirectIfAuthenticated,
} from '@/components/common/RequireAuth';
import { RequireWorkspace } from '@/components/common/RequireWorkspace';
import { LoadingState } from '@/components/common/States';
import { env } from '@/config/env';

// The editor pulls in Jodit, which is large — it loads only when someone
// actually opens a blog rather than on first paint.
const DashboardPage = lazy(() => import('@/pages/Dashboard/DashboardPage'));
const BlogListPage = lazy(() => import('@/pages/Blogs/BlogListPage'));
const BlogEditorPage = lazy(() => import('@/pages/Blogs/BlogEditorPage'));
const PostListPage = lazy(() => import('@/pages/Posts/PostListPage'));
const PostComposerPage = lazy(() => import('@/pages/Posts/PostComposerPage'));
const LibraryListPage = lazy(() => import('@/pages/Libraries/LibraryListPage'));
const LibraryDetailPage = lazy(
  () => import('@/pages/Libraries/LibraryDetailPage')
);
const SchedulePage = lazy(() => import('@/pages/Schedule/SchedulePage'));
const YouTubeLayout = lazy(() => import('@/pages/YouTube/YouTubeLayout'));
const YouTubeDashboardPage = lazy(
  () => import('@/pages/YouTube/YouTubeDashboardPage')
);
const YouTubeContentPage = lazy(
  () => import('@/pages/YouTube/YouTubeContentPage')
);
const YouTubePlaylistsPage = lazy(
  () => import('@/pages/YouTube/YouTubePlaylistsPage')
);
const YouTubeUploadDefaultsPage = lazy(
  () => import('@/pages/YouTube/YouTubeUploadDefaultsPage')
);
const YouTubeVideoEditPage = lazy(
  () => import('@/pages/YouTube/YouTubeVideoEditPage')
);
const MediaPage = lazy(() => import('@/pages/Media/MediaPage'));
const DistributionListPage = lazy(
  () => import('@/pages/Distributions/DistributionListPage')
);
const DistributionDetailPage = lazy(
  () => import('@/pages/Distributions/DistributionDetailPage')
);
const SocialAccountsPage = lazy(
  () => import('@/pages/SocialAccounts/SocialAccountsPage')
);
const PlatformPresetsPage = lazy(
  () => import('@/pages/PlatformPresets/PlatformPresetsPage')
);
const AiUsagePage = lazy(() => import('@/pages/AiUsage/AiUsagePage'));
const JobsPage = lazy(() => import('@/pages/Jobs/JobsPage'));
const ActivityLogPage = lazy(
  () => import('@/pages/ActivityLog/ActivityLogPage')
);

const LandingPage = lazy(() => import('@/pages/Public/LandingPage'));
const PricingPage = lazy(() => import('@/pages/Public/PricingPage'));
const PrivacyPolicyPage = lazy(
  () => import('@/pages/Public/PrivacyPolicyPage')
);
const TermsOfServicePage = lazy(
  () => import('@/pages/Public/TermsOfServicePage')
);

const LoginPage = lazy(() => import('@/pages/Auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/Auth/RegisterPage'));
const ForgotPasswordPage = lazy(
  () => import('@/pages/Auth/ForgotPasswordPage')
);
const ResetPasswordPage = lazy(() => import('@/pages/Auth/ResetPasswordPage'));
const AcceptInvitePage = lazy(() => import('@/pages/Auth/AcceptInvitePage'));

const AccountSettingsPage = lazy(
  () => import('@/pages/Settings/AccountSettingsPage')
);
const WorkspaceSettingsPage = lazy(
  () => import('@/pages/Settings/WorkspaceSettingsPage')
);
const MembersPage = lazy(() => import('@/pages/Settings/MembersPage'));
const MediaKitLayout = /* @__PURE__ */ lazy(() => import('@/pages/MediaKit/MediaKitLayout'));
const MediaKitEditorPage = /* @__PURE__ */ lazy(
  () => import('@/pages/MediaKit/MediaKitEditorPage')
);
const MediaKitInquiriesPage = /* @__PURE__ */ lazy(
  () => import('@/pages/MediaKit/MediaKitInquiriesPage')
);
const MediaKitPublicPage = /* @__PURE__ */ lazy(
  () => import('@/pages/Public/MediaKitPublicPage')
);
/*
 * The pages of Analytics and the media kit are marked pure so that a build
 * with a feature switched off drops them entirely. Their routes below are
 * already removed at build time; without the mark, the `lazy()` calls would
 * survive on their own and each page would still be emitted as a file that
 * nothing loads.
 */
const AnalyticsLayout = /* @__PURE__ */ lazy(
  () => import('@/pages/Analytics/AnalyticsLayout')
);
const AnalyticsOverviewPage = /* @__PURE__ */ lazy(
  () => import('@/pages/Analytics/AnalyticsOverviewPage')
);
const AnalyticsPostsPage = /* @__PURE__ */ lazy(
  () => import('@/pages/Analytics/AnalyticsPostsPage')
);
const AnalyticsBestTimesPage = /* @__PURE__ */ lazy(
  () => import('@/pages/Analytics/AnalyticsBestTimesPage')
);
const AnalyticsReportsPage = /* @__PURE__ */ lazy(
  () => import('@/pages/Analytics/AnalyticsReportsPage')
);
const AnalyticsReportPrintPage = /* @__PURE__ */ lazy(
  () => import('@/pages/Analytics/AnalyticsReportPrintPage')
);
const BillingPage = lazy(() => import('@/pages/Billing/BillingPage'));

/**
 * A redirect that preserves the query string, and optionally an `:id`.
 *
 * `<Navigate>` alone drops `location.search` unless it is spelled out, and it
 * cannot interpolate a route param at all. Both matter here: `/social-accounts`
 * is where OAuth returns with `platform`, `connected` and `message` appended,
 * and `/blogs/:id` has to land on the same blog it named.
 */
// eslint-disable-next-line react-refresh/only-export-components
function RedirectTo({ to }: { to: string }) {
  const { search, hash } = useLocation();
  const params = useParams();
  const resolved = to.replace(
    /:(\w+)/g,
    (match, name: string) => params[name] ?? match
  );
  return <Navigate to={`${resolved}${search}${hash}`} replace />;
}

/**
 * The last route. Two different questions arrive here.
 *
 * Everything the app owns moved under `/app`, so a bookmark, an email link or a
 * platform callback aimed at the old flat path — `/social-accounts?connected=1`
 * — is not a mistake, it is a correct URL from last week. Those are forwarded
 * with their query string intact, which matters most for the OAuth return,
 * where the query *is* the result of the connection.
 *
 * Anything else is a real miss and goes to the landing page rather than the
 * app, because whoever typed it may not have an account at all.
 *
 * Listed by first segment rather than matched loosely: `/pricing` must stay a
 * marketing page, and a prefix test would have swallowed it.
 */
const MOVED_UNDER_APP = new Set([
  'posts',
  'libraries',
  'schedule',
  'media',
  'distributions',
  'social-accounts',
  'settings',
  'cadence',
  'upcoming',
  'accounts',
  'platform-presets',
  'ai-usage',
  'activity-log',
  'jobs',
  'blogs',
]);

// eslint-disable-next-line react-refresh/only-export-components
function MovedOrMissing() {
  const { pathname, search, hash } = useLocation();
  const first = pathname.split('/')[1] ?? '';

  if (MOVED_UNDER_APP.has(first)) {
    return <Navigate to={`/app${pathname}${search}${hash}`} replace />;
  }
  return <Navigate to="/" replace />;
}

// Route-level Suspense boundary. Local to this module by design — it exists
// only to wrap the lazy imports above, and the file's export is the router.
// eslint-disable-next-line react-refresh/only-export-components
function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  /*
   * The marketing site. Root belongs to it, not to the app.
   *
   * These are the only routes a search engine or a platform reviewer ever sees,
   * and Meta App Review opens the policy pages signed out — so they are
   * deliberately outside every guard, with no workspace and no token involved.
   */
  {
    path: '/',
    element: (
      <Lazy>
        <LandingPage />
      </Lazy>
    ),
  },
  {
    path: '/pricing',
    element: (
      <Lazy>
        <PricingPage />
      </Lazy>
    ),
  },
  {
    path: '/privacy-policy',
    element: (
      <Lazy>
        <PrivacyPolicyPage />
      </Lazy>
    ),
  },
  {
    path: '/terms-of-service',
    element: (
      <Lazy>
        <TermsOfServicePage />
      </Lazy>
    ),
  },

  /*
   * A creator's public media kit. Outside every guard for the same reason as
   * the pages above: whoever opens it is a sponsor with no account.
   *
   * Only in builds where the media kit is switched on. Without it the path
   * falls through to the last route, like any other unknown address.
   */
  ...(env.mediaKitEnabled
    ? [
        {
          path: '/kit/:slug',
          element: (
            <Lazy>
              <MediaKitPublicPage />
            </Lazy>
          ),
        },
      ]
    : []),

  // Signed-out routes sit outside the guarded tree. `/reset-password` is
  // deliberately not wrapped in RedirectIfAuthenticated: someone already signed
  // in on this browser may still be following a reset link from their email.
  {
    path: '/login',
    element: (
      <RedirectIfAuthenticated>
        <Lazy>
          <LoginPage />
        </Lazy>
      </RedirectIfAuthenticated>
    ),
  },
  {
    path: '/register',
    element: (
      <RedirectIfAuthenticated>
        <Lazy>
          <RegisterPage />
        </Lazy>
      </RedirectIfAuthenticated>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <RedirectIfAuthenticated>
        <Lazy>
          <ForgotPasswordPage />
        </Lazy>
      </RedirectIfAuthenticated>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Lazy>
        <ResetPasswordPage />
      </Lazy>
    ),
  },
  {
    path: '/invite/:token',
    element: (
      <Lazy>
        <AcceptInvitePage />
      </Lazy>
    ),
  },

  /*
   * The client report, laid out for printing. Behind the same sign-in and
   * workspace as the app, and outside its frame: a page that is going to be
   * saved as a PDF has no business carrying a sidebar.
   */
  ...(env.analyticsEnabled
    ? [
        {
          path: '/print/analytics-report',
          element: (
            <RequireAuth>
              <RequireWorkspace>
                <Lazy>
                  <AnalyticsReportPrintPage />
                </Lazy>
              </RequireWorkspace>
            </RequireAuth>
          ),
        },
      ]
    : []),

  {
    path: '/app',
    element: (
      <RequireAuth>
        <RequireWorkspace>
          <AppLayout />
        </RequireWorkspace>
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <Lazy>
            <DashboardPage />
          </Lazy>
        ),
      },
      {
        path: 'posts',
        element: (
          <Lazy>
            <PostListPage />
          </Lazy>
        ),
      },
      {
        path: 'posts/new',
        element: (
          <Lazy>
            <PostComposerPage />
          </Lazy>
        ),
      },
      {
        path: 'posts/:id',
        element: (
          <Lazy>
            <PostComposerPage />
          </Lazy>
        ),
      },
      {
        path: 'libraries',
        element: (
          <Lazy>
            <LibraryListPage />
          </Lazy>
        ),
      },
      {
        path: 'libraries/:id',
        element: (
          <Lazy>
            <LibraryDetailPage />
          </Lazy>
        ),
      },
      {
        path: 'schedule',
        element: (
          <Lazy>
            <SchedulePage />
          </Lazy>
        ),
      },
      {
        // The YouTube section. The channel rides in `?channel=`.
        path: 'youtube',
        element: (
          <Lazy>
            <YouTubeLayout />
          </Lazy>
        ),
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <YouTubeDashboardPage />
              </Lazy>
            ),
          },
          {
            path: 'content',
            element: (
              <Lazy>
                <YouTubeContentPage />
              </Lazy>
            ),
          },
          {
            path: 'playlists',
            element: (
              <Lazy>
                <YouTubePlaylistsPage />
              </Lazy>
            ),
          },
          {
            path: 'defaults',
            element: (
              <Lazy>
                <YouTubeUploadDefaultsPage />
              </Lazy>
            ),
          },
          {
            path: 'videos/:videoId',
            element: (
              <Lazy>
                <YouTubeVideoEditPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        path: 'media',
        element: (
          <Lazy>
            <MediaPage />
          </Lazy>
        ),
      },
      /*
       * Delivery. Publishing is asynchronous — a run generates, waits for
       * approval, then posts — and `post.status` only records that someone
       * released the draft, at the moment they did. It says nothing about
       * whether a platform accepted it, so these two screens remain the only
       * place that answers "did it actually go out".
       *
       * The list lost its route in the nav change on the grounds that it
       * duplicated Posts. It does not: Posts lists what you wrote, this lists
       * what happened to it. Without it a run was reachable only from the
       * redirect fired once on publish, and closing that tab lost it.
       */
      {
        path: 'distributions',
        element: (
          <Lazy>
            <DistributionListPage />
          </Lazy>
        ),
      },
      /*
       * Read-only now — the review affordances are gone with the approval
       * step. Kept as a route rather than folded into the post page because a
       * blog run has no `postId`, so this is the only place its targets can be
       * seen and a failed one retried.
       */
      {
        path: 'distributions/:id',
        element: (
          <Lazy>
            <DistributionDetailPage />
          </Lazy>
        ),
      },
      {
        path: 'social-accounts',
        element: (
          <Lazy>
            <SocialAccountsPage />
          </Lazy>
        ),
      },
      // Analytics: four tabs that share a range and a set of accounts.
      ...(env.analyticsEnabled
        ? [
            {
              path: 'analytics',
              element: (
                <Lazy>
                  <AnalyticsLayout />
                </Lazy>
              ),
              children: [
                {
                  index: true,
                  element: (
                    <Lazy>
                      <AnalyticsOverviewPage />
                    </Lazy>
                  ),
                },
                {
                  path: 'posts',
                  element: (
                    <Lazy>
                      <AnalyticsPostsPage />
                    </Lazy>
                  ),
                },
                {
                  path: 'best-times',
                  element: (
                    <Lazy>
                      <AnalyticsBestTimesPage />
                    </Lazy>
                  ),
                },
                {
                  path: 'reports',
                  element: (
                    <Lazy>
                      <AnalyticsReportsPage />
                    </Lazy>
                  ),
                },
              ],
            },
          ]
        : []),
      // The media kit: its editor, and the inbox its contact form fills.
      ...(env.mediaKitEnabled
        ? [
            {
              path: 'media-kit',
              element: (
                <Lazy>
                  <MediaKitLayout />
                </Lazy>
              ),
              children: [
                {
                  index: true,
                  element: (
                    <Lazy>
                      <MediaKitEditorPage />
                    </Lazy>
                  ),
                },
                {
                  path: 'inquiries',
                  element: (
                    <Lazy>
                      <MediaKitInquiriesPage />
                    </Lazy>
                  ),
                },
              ],
            },
          ]
        : []),
      /*
       * Billing, a top-level screen rather than a settings section.
       *
       * It is the one former settings screen a person opens deliberately —
       * because a trial is ending, a card failed, or a quota is about to bite —
       * and three other screens deep-link to it for those reasons.
       */
      {
        path: 'billing',
        element: (
          <Lazy>
            <BillingPage />
          </Lazy>
        ),
      },
      /*
       * Settings, as a nested area with its own rail.
       *
       * Six screens moved in here when the sidebar went to seven items. Each
       * old path redirects rather than 404s — see the block below — because
       * they are in browser histories and in at least one bookmark.
       */
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/app/settings/workspace" replace />,
          },
          {
            path: 'workspace',
            element: (
              <Lazy>
                <WorkspaceSettingsPage />
              </Lazy>
            ),
          },
          {
            path: 'account',
            element: (
              <Lazy>
                <AccountSettingsPage />
              </Lazy>
            ),
          },
          {
            path: 'members',
            element: (
              <Lazy>
                <MembersPage />
              </Lazy>
            ),
          },
          {
            /*
             * Billing moved up to the sidebar. This must keep resolving and it
             * must keep the query string: both gateways send the browser back
             * to `${APP_WEB_URL}/app/settings/billing?checkout=success`, which
             * is configured backend-side, and `checkout` is the whole payload —
             * dropping it strands a paying customer on a page that never tells
             * them their payment landed.
             */
            path: 'billing',
            element: <RedirectTo to="/app/billing" />,
          },
          {
            path: 'platforms',
            element: (
              <Lazy>
                <PlatformPresetsPage />
              </Lazy>
            ),
          },
          {
            path: 'usage',
            element: (
              <Lazy>
                <AiUsagePage />
              </Lazy>
            ),
          },
          {
            path: 'activity',
            element: (
              <Lazy>
                <ActivityLogPage />
              </Lazy>
            ),
          },
          {
            path: 'jobs',
            element: (
              <Lazy>
                <JobsPage />
              </Lazy>
            ),
          },
          {
            path: 'blogs',
            element: (
              <Lazy>
                <BlogListPage />
              </Lazy>
            ),
          },
          {
            path: 'blogs/new',
            element: (
              <Lazy>
                <BlogEditorPage />
              </Lazy>
            ),
          },
          {
            path: 'blogs/:id',
            element: (
              <Lazy>
                <BlogEditorPage />
              </Lazy>
            ),
          },
        ],
      },

      // Redirects for everything the nav change moved. `/distributions` is
      // absent because it is a destination again, above.
      { path: 'upcoming', element: <RedirectTo to="/app/schedule" /> },
      { path: 'cadence', element: <RedirectTo to="/app/libraries" /> },
      { path: 'accounts', element: <RedirectTo to="/app/social-accounts" /> },
      {
        path: 'platform-presets',
        element: <RedirectTo to="/app/settings/platforms" />,
      },
      { path: 'ai-usage', element: <RedirectTo to="/app/settings/usage" /> },
      {
        path: 'activity-log',
        element: <RedirectTo to="/app/settings/activity" />,
      },
      { path: 'jobs', element: <RedirectTo to="/app/settings/jobs" /> },
      { path: 'blogs', element: <RedirectTo to="/app/settings/blogs" /> },
      {
        path: 'blogs/new',
        element: <RedirectTo to="/app/settings/blogs/new" />,
      },
      {
        path: 'blogs/:id',
        element: <RedirectTo to="/app/settings/blogs/:id" />,
      },

      { path: '*', element: <Navigate to="/app" replace /> },
    ],
  },

  { path: '*', element: <MovedOrMissing /> },
]);
